import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Print as PrintIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { customerService } from '../services/customer.service';
import { transactionService } from '../services/transaction.service';
import { moldService } from '../services/mold.service';
import { Customer, Transaction } from '../types';
import * as XLSX from 'xlsx';

type PeriodType = 'today' | 'week' | 'month' | 'quarter' | 'half' | 'year' | 'custom';

interface DateRange {
  startDate: Date;
  endDate: Date;
}

interface MoldProductionData {
  moldId: string;
  moldNumber: string;
  moldName: string;
  customerName: string;
  checkOutCount: number;
  totalShots: number;
  averageShots: number;
  transactions?: Transaction[]; // 상세 내역
}

export interface ReportData {
  periodStart: string;
  periodEnd: string;
  totalProduction: number;
  averageDaily: number;
  maxDaily: number;
  activeMolds: number;
  moldDetails: Array<{
    moldNumber: string;
    moldName: string;
    customerName: string;
    totalShots: number;
    productionDays: number;
  }>;
}

// 리포트 데이터 생성 함수 (외부에서 사용 가능)
export const generateReportData = async (
  startDate: Date,
  endDate: Date,
  customerId?: string
): Promise<ReportData> => {
  try {
    // 1. 기간별 입출고 데이터 가져오기
    const transactions = await transactionService.getTransactionsByDateRange(
      startDate,
      endDate
    );

    // 2. 입고(check-in) 건만 필터링 (쇼트가 기록된 건)
    const checkInTransactions = transactions.filter(
      (t) => t.type === 'check-in' && t.shots && t.shots > 0
    );

    // 3. moldId별 집계
    const moldMap = new Map<string, {
      checkOutCount: number;
      totalShots: number;
      moldId: string;
      daysSet: Set<string>;
    }>();

    checkInTransactions.forEach((transaction) => {
      const existing = moldMap.get(transaction.moldId) || {
        moldId: transaction.moldId,
        checkOutCount: 0,
        totalShots: 0,
        daysSet: new Set<string>(),
      };

      existing.checkOutCount += 1;
      existing.totalShots += transaction.shots || 0;
      
      // 날짜 추가
      const date = transaction.timestamp?.toDate?.();
      if (date) {
        const dateKey = date.toLocaleDateString('ko-KR');
        existing.daysSet.add(dateKey);
      }
      
      moldMap.set(transaction.moldId, existing);
    });

    // 4. 금형 정보 가져오기
    const molds = await moldService.getMolds();
    const moldsMap = new Map(molds.map((m) => [m.id!, m]));

    // 5. 금형별 상세 데이터 구성
    const moldDetails = Array.from(moldMap.values())
      .map((item) => {
        const mold = moldsMap.get(item.moldId);
        if (!mold) return null;

        // 고객사 필터 적용
        if (customerId && customerId !== 'all' && mold.customerId !== customerId) {
          return null;
        }

        return {
          moldNumber: mold.moldNumber,
          moldName: mold.moldName,
          customerName: mold.customerName || '미등록',
          totalShots: item.totalShots,
          productionDays: item.daysSet.size,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
      .sort((a, b) => b.totalShots - a.totalShots);

    // 6. 일별 생산량 계산
    const dailyMap = new Map<string, number>();
    checkInTransactions.forEach((trans) => {
      const date = trans.timestamp?.toDate?.();
      if (date) {
        const dateKey = date.toLocaleDateString('ko-KR');
        dailyMap.set(dateKey, (dailyMap.get(dateKey) || 0) + (trans.shots || 0));
      }
    });

    const dailyProduction = Array.from(dailyMap.values());
    const totalProduction = moldDetails.reduce((sum, m) => sum + m.totalShots, 0);
    const averageDaily = dailyProduction.length > 0 
      ? Math.round(totalProduction / dailyProduction.length) 
      : 0;
    const maxDaily = dailyProduction.length > 0 
      ? Math.max(...dailyProduction) 
      : 0;

    return {
      periodStart: startDate.toLocaleDateString('ko-KR'),
      periodEnd: endDate.toLocaleDateString('ko-KR'),
      totalProduction,
      averageDaily,
      maxDaily,
      activeMolds: moldDetails.length,
      moldDetails,
    };
  } catch (error) {
    console.error('리포트 데이터 생성 실패:', error);
    throw error;
  }
};

const ProductionReport: React.FC = () => {
  const [period, setPeriod] = useState<PeriodType>('month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<string>('all');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [productionData, setProductionData] = useState<MoldProductionData[]>([]);
  const [expandedMoldId, setExpandedMoldId] = useState<string | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [dailyChartData, setDailyChartData] = useState<any[]>([]);
  const [topMoldsChartData, setTopMoldsChartData] = useState<any[]>([]);
  const [customerChartData, setCustomerChartData] = useState<any[]>([]);
  const [summary, setSummary] = useState({
    totalShots: 0,
    totalCheckOuts: 0,
    totalMolds: 0,
    averageShots: 0,
  });

  // 차트 ref
  const dailyChartRef = useRef<HTMLDivElement>(null);
  const topMoldsChartRef = useRef<HTMLDivElement>(null);
  const customerChartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const data = await customerService.getAll();
      setCustomers(data);
    } catch (error) {
      console.error('고객사 로딩 실패:', error);
    }
  };

  const getDateRange = (): DateRange => {
    const now = new Date();
    let startDate = new Date();
    let endDate = new Date();

    switch (period) {
      case 'today':
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'week':
        const dayOfWeek = now.getDay();
        startDate.setDate(now.getDate() - dayOfWeek);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        endDate = new Date(now.getFullYear(), (quarter + 1) * 3, 0, 23, 59, 59, 999);
        break;
      case 'half':
        const half = now.getMonth() < 6 ? 0 : 6;
        startDate = new Date(now.getFullYear(), half, 1);
        endDate = new Date(now.getFullYear(), half + 6, 0, 23, 59, 59, 999);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
        }
        break;
    }

    return { startDate, endDate };
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    setExpandedMoldId(null); // 펼쳐진 항목 초기화
    try {
      const dateRange = getDateRange();
      
      // 1. 기간별 입출고 데이터 가져오기
      const transactions = await transactionService.getTransactionsByDateRange(
        dateRange.startDate,
        dateRange.endDate
      );

      // 2. 입고(check-in) 건만 필터링 (쇼트가 기록된 건)
      const checkInTransactions = transactions.filter(
        (t) => t.type === 'check-in' && t.shots && t.shots > 0
      );

      // 3. moldId별 집계
      const moldMap = new Map<string, {
        checkOutCount: number;
        totalShots: number;
        moldId: string;
      }>();

      checkInTransactions.forEach((transaction) => {
        const existing = moldMap.get(transaction.moldId) || {
          moldId: transaction.moldId,
          checkOutCount: 0,
          totalShots: 0,
        };

        existing.checkOutCount += 1;
        existing.totalShots += transaction.shots || 0;
        moldMap.set(transaction.moldId, existing);
      });

      // 4. 금형 정보 가져오기
      const molds = await moldService.getMolds();
      const moldsMap = new Map(molds.map((m) => [m.id!, m]));

      // 5. 데이터 구성
      const data: MoldProductionData[] = Array.from(moldMap.values())
        .map((item) => {
          const mold = moldsMap.get(item.moldId);
          if (!mold) return null;

          // 고객사 필터 적용
          if (selectedCustomer !== 'all' && mold.customerId !== selectedCustomer) {
            return null;
          }

          return {
            moldId: item.moldId,
            moldNumber: mold.moldNumber,
            moldName: mold.moldName,
            customerName: mold.customerName || '미등록',
            checkOutCount: item.checkOutCount,
            totalShots: item.totalShots,
            averageShots: Math.round(item.totalShots / item.checkOutCount),
          };
        })
        .filter((item): item is MoldProductionData => item !== null)
        .sort((a, b) => b.totalShots - a.totalShots); // 총 쇼트 내림차순

      setProductionData(data);

      // 6. 요약 정보 계산
      const totalShots = data.reduce((sum, item) => sum + item.totalShots, 0);
      const totalCheckOuts = data.reduce((sum, item) => sum + item.checkOutCount, 0);
      const totalMolds = data.length;
      const averageShots = totalCheckOuts > 0 ? Math.round(totalShots / totalCheckOuts) : 0;

      setSummary({
        totalShots,
        totalCheckOuts,
        totalMolds,
        averageShots,
      });

      // 7. 차트 데이터 생성
      // 일별 생산 추이
      const dailyMap = new Map<string, number>();
      checkInTransactions.forEach((trans) => {
        const date = trans.timestamp?.toDate?.();
        if (date) {
          const dateKey = date.toLocaleDateString('ko-KR', {
            month: '2-digit',
            day: '2-digit',
          }).replace(/\. /g, '/').replace('.', '');
          dailyMap.set(dateKey, (dailyMap.get(dateKey) || 0) + (trans.shots || 0));
        }
      });
      const dailyData = Array.from(dailyMap.entries())
        .map(([date, shots]) => ({ date, shots }))
        .sort((a, b) => {
          const [aMonth, aDay] = a.date.split('/').map(Number);
          const [bMonth, bDay] = b.date.split('/').map(Number);
          return aMonth !== bMonth ? aMonth - bMonth : aDay - bDay;
        });
      setDailyChartData(dailyData);

      // Top 10 금형
      const top10 = data.slice(0, 10).map((item) => ({
        name: item.moldNumber,
        shots: item.totalShots,
      }));
      setTopMoldsChartData(top10);

      // 고객사별 비율
      const customerMap = new Map<string, number>();
      data.forEach((item) => {
        customerMap.set(
          item.customerName,
          (customerMap.get(item.customerName) || 0) + item.totalShots
        );
      });
      const customerData = Array.from(customerMap.entries())
        .map(([name, shots]) => ({ name, value: shots }))
        .sort((a, b) => b.value - a.value);
      setCustomerChartData(customerData);

    } catch (err) {
      console.error('리포트 생성 실패:', err);
      setError('리포트 생성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 현재 창에서 차트 캡처 후 새 창에 전달하는 방식
  const handlePrint = async () => {
    try {
      setLoading(true);

      // 1. 기존 차트 DOM 요소 찾기
      const dailyChartElement = document.querySelector('.recharts-wrapper') as HTMLElement;
      if (!dailyChartElement) {
        alert('차트를 찾을 수 없습니다. 먼저 리포트를 조회해주세요.');
        setLoading(false);
        return;
      }

      // 2. html2canvas 동적 로드
      const html2canvas = (await import('html2canvas')).default;

      // 3. 모든 차트 캡처
      const chartElements = document.querySelectorAll('.recharts-wrapper');
      const chartImages: string[] = [];

      for (const chartEl of Array.from(chartElements)) {
        const canvas = await html2canvas(chartEl as HTMLElement, {
          scale: 2,
          backgroundColor: '#ffffff',
          logging: false,
        });
        chartImages.push(canvas.toDataURL('image/png'));
      }

      // 4. 날짜 및 고객사 정보
      const dateRange = getDateRange();
      const customerName = selectedCustomer === 'all' 
        ? '전체' 
        : customers.find(c => c.id === selectedCustomer)?.name || '';

      // 5. 새 창 열기
      const printWindow = window.open('', '_blank', 'width=1000,height=800');
      if (!printWindow) {
        alert('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
        setLoading(false);
        return;
      }

      // 6. HTML 생성
      const printHtml = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>생산 실적 리포트</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    @page { size: A4; margin: 15mm; }
    body { font-family: "Pretendard Variable", "Pretendard", sans-serif; padding: 20px; color: #000; }
    .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #1976d2; padding-bottom: 20px; }
    h1 { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
    .info { font-size: 14px; color: #666; }
    .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 30px; }
    .summary-card { border: 1px solid #ddd; border-radius: 8px; padding: 15px; text-align: center; }
    .summary-card h3 { font-size: 12px; color: #666; margin-bottom: 8px; }
    .summary-card p { font-size: 20px; font-weight: bold; color: #1976d2; }
    .chart-section { margin-bottom: 30px; page-break-inside: avoid; }
    .chart-section h2 { font-size: 16px; font-weight: 600; margin-bottom: 15px; }
    .chart-section img { width: 100%; height: auto; border: 1px solid #ddd; border-radius: 4px; }
    .table-section { page-break-before: always; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th { background-color: #f0f0f0; padding: 10px; text-align: left; border: 1px solid #ddd; font-weight: 600; }
    td { padding: 8px; border: 1px solid #ddd; }
    @media print { body { padding: 10px; } .chart-section { page-break-inside: avoid; } }
  </style>
</head>
<body>
  <div class="header">
    <h1>📊 생산 실적 리포트</h1>
    <div class="info">
      <p>기간: ${dateRange.startDate.toLocaleDateString('ko-KR')} ~ ${dateRange.endDate.toLocaleDateString('ko-KR')}</p>
      <p>고객사: ${customerName}</p>
      <p>생성일: ${new Date().toLocaleDateString('ko-KR')} ${new Date().toLocaleTimeString('ko-KR')}</p>
    </div>
  </div>
  <div class="summary">
    <div class="summary-card"><h3>총 생산량</h3><p>${summary.totalShots.toLocaleString()}</p></div>
    <div class="summary-card"><h3>출고 횟수</h3><p>${summary.totalCheckOuts.toLocaleString()}</p></div>
    <div class="summary-card"><h3>사용 금형</h3><p>${summary.totalMolds.toLocaleString()}</p></div>
    <div class="summary-card"><h3>평균 생산량/건</h3><p>${summary.averageShots.toLocaleString()}</p></div>
  </div>
  <div class="chart-section">
    <h2>📈 일별 생산 추이</h2>
    <img src="${chartImages[0]}" alt="일별 생산 추이" />
  </div>
  <div class="chart-section">
    <h2>🏆 금형별 생산량 Top 10</h2>
    <img src="${chartImages[1]}" alt="금형별 생산량" />
  </div>
  <div class="chart-section">
    <h2>🏢 고객사별 생산 비율</h2>
    <img src="${chartImages[2]}" alt="고객사별 비율" />
  </div>
  <div class="table-section">
    <h2>🔧 금형별 생산 실적</h2>
    <table>
      <thead>
        <tr><th>No</th><th>금형번호</th><th>금형명</th><th>고객사</th><th>출고횟수</th><th>총쇼트</th><th>평균쇼트</th></tr>
      </thead>
      <tbody>
        ${productionData.map((item, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${item.moldNumber}</td>
            <td>${item.moldName}</td>
            <td>${item.customerName}</td>
            <td>${item.checkOutCount.toLocaleString()}</td>
            <td>${item.totalShots.toLocaleString()}</td>
            <td>${item.averageShots.toLocaleString()}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  </div>
  <script>
    window.onload = function() {
      setTimeout(() => {
        window.print();
      }, 500);
    };
  </script>
</body>
</html>
      `;

      printWindow.document.write(printHtml);
      printWindow.document.close();

      setLoading(false);
    } catch (error) {
      console.error('인쇄 실패:', error);
      alert('인쇄 준비 중 오류가 발생했습니다: ' + (error instanceof Error ? error.message : String(error)));
      setLoading(false);
    }
  };

  const handleExcelDownload = async () => {
    try {
      // 1. 워크북 생성
      const wb = XLSX.utils.book_new();

      // 2. Sheet1: 요약
      const dateRange = getDateRange();
      const summaryData = [
        ['생산 실적 리포트'],
        [],
        ['기간', `${dateRange.startDate.toLocaleDateString('ko-KR')} ~ ${dateRange.endDate.toLocaleDateString('ko-KR')}`],
        ['고객사', selectedCustomer === 'all' ? '전체' : customers.find(c => c.id === selectedCustomer)?.name || ''],
        [],
        ['총 생산량 (쇼트)', summary.totalShots.toLocaleString()],
        ['출고 횟수 (건)', summary.totalCheckOuts.toLocaleString()],
        ['사용 금형 (개)', summary.totalMolds.toLocaleString()],
        ['평균 생산량/건 (쇼트)', summary.averageShots.toLocaleString()],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, ws1, '요약');

      // 3. Sheet2: 금형별 상세
      const moldDetailData = [
        ['금형번호', '금형명', '고객사', '출고횟수', '총쇼트', '평균쇼트'],
        ...productionData.map(item => [
          item.moldNumber,
          item.moldName,
          item.customerName,
          item.checkOutCount,
          item.totalShots,
          item.averageShots,
        ]),
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(moldDetailData);
      XLSX.utils.book_append_sheet(wb, ws2, '금형별 상세');

      // 4. Sheet3: 입출고 내역 - 자동 로드
      const transactionDetailData: any[] = [
        ['금형번호', '금형명', '고객사', '입고일', '쇼트', '목적'],
      ];
      
      // 모든 입고 내역 조회
      const allTransactions = await transactionService.getTransactionsByDateRange(
        dateRange.startDate,
        dateRange.endDate
      );
      
      // 입고 건만 필터링
      const checkInTransactions = allTransactions
        .filter((t) => t.type === 'check-in' && t.shots && t.shots > 0)
        .sort((a, b) => {
          const aTime = a.timestamp?.toDate?.() || new Date(0);
          const bTime = b.timestamp?.toDate?.() || new Date(0);
          return bTime.getTime() - aTime.getTime(); // 최신순
        });
      
      // productionData의 moldId 맵 생성
      const moldMap = new Map(productionData.map(m => [m.moldId, m]));
      
      // 입출고 내역 생성
      checkInTransactions.forEach(trans => {
        const mold = moldMap.get(trans.moldId);
        if (mold) {
          const checkInDate = trans.timestamp?.toDate?.();
          transactionDetailData.push([
            mold.moldNumber,
            mold.moldName,
            mold.customerName,
            checkInDate ? checkInDate.toLocaleDateString('ko-KR') + ' ' + checkInDate.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }) : '-',
            trans.shots || 0,
            trans.purpose || '-',
          ]);
        }
      });
      
      const ws3 = XLSX.utils.aoa_to_sheet(transactionDetailData);
      XLSX.utils.book_append_sheet(wb, ws3, '입출고 내역');

      // 5. 파일 다운로드
      const now = new Date();
      const fileName = `생산실적_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error('Excel 다운로드 실패:', error);
      alert('Excel 파일 다운로드에 실패했습니다.');
    }
  };

  const handleRowClick = async (moldId: string) => {
    // 이미 펼쳐져 있으면 닫기
    if (expandedMoldId === moldId) {
      setExpandedMoldId(null);
      return;
    }

    // 이미 데이터가 있으면 바로 펼치기
    const existingData = productionData.find((item) => item.moldId === moldId);
    if (existingData?.transactions) {
      setExpandedMoldId(moldId);
      return;
    }

    // 데이터 로딩
    setLoadingDetails(true);
    try {
      const dateRange = getDateRange();
      const allTransactions = await transactionService.getTransactionsByDateRange(
        dateRange.startDate,
        dateRange.endDate
      );

      // 해당 금형의 입고 건만 필터링
      const moldTransactions = allTransactions
        .filter((t) => t.moldId === moldId && t.type === 'check-in' && t.shots && t.shots > 0)
        .sort((a, b) => {
          const aTime = a.timestamp?.toDate?.() || new Date(0);
          const bTime = b.timestamp?.toDate?.() || new Date(0);
          return bTime.getTime() - aTime.getTime();
        });

      // productionData 업데이트
      setProductionData((prev) =>
        prev.map((item) =>
          item.moldId === moldId ? { ...item, transactions: moldTransactions } : item
        )
      );

      setExpandedMoldId(moldId);
    } catch (err) {
      console.error('상세 내역 로딩 실패:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).replace(/\. /g, '/').replace('.', '');
  };

  const dateRange = getDateRange();

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* 필터 영역 - 화면에만 표시 */}
      <Card
        className="no-print"
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          생산 실적 리포트
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>기간 선택</InputLabel>
              <Select
                value={period}
                label="기간 선택"
                onChange={(e) => setPeriod(e.target.value as PeriodType)}
              >
                <MenuItem value="today">오늘</MenuItem>
                <MenuItem value="week">이번 주</MenuItem>
                <MenuItem value="month">이번 달</MenuItem>
                <MenuItem value="quarter">이번 분기</MenuItem>
                <MenuItem value="half">상/하반기</MenuItem>
                <MenuItem value="year">올해</MenuItem>
                <MenuItem value="custom">사용자 정의</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>고객사</InputLabel>
              <Select
                value={selectedCustomer}
                label="고객사"
                onChange={(e) => setSelectedCustomer(e.target.value)}
              >
                <MenuItem value="all">전체</MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {period === 'custom' && (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                type="date"
                label="시작일"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                type="date"
                label="종료일"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          )}

          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2}
          >
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleGenerate}
              disabled={loading}
              sx={{ minWidth: { xs: '100%', sm: 140 }, height: 40 }}
            >
              리포트 생성
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{ minWidth: { xs: '100%', sm: 140 }, height: 40 }}
            >
              인쇄
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExcelDownload}
              sx={{ minWidth: { xs: '100%', sm: 140 }, height: 40 }}
            >
              Excel
            </Button>
          </Stack>
        </Stack>
      </Card>

      {/* A4 리포트 영역 */}
      <Box
        className="a4-report"
        sx={{
          backgroundColor: 'white',
          color: '#000',
          minHeight: '297mm',
          width: '100%',
          maxWidth: '210mm',
          margin: '0 auto',
          padding: '20mm',
          boxShadow: '0 0 10px rgba(0,0,0,0.1)',
          '@media print': {
            boxShadow: 'none',
            margin: 0,
            maxWidth: 'none',
          },
        }}
      >
        {/* 리포트 헤더 */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#1976d2' }}>
            EGMMS 생산 실적 리포트
          </Typography>
          <Typography variant="subtitle1" sx={{ color: '#666' }}>
            기간: {formatDate(dateRange.startDate)} ~ {formatDate(dateRange.endDate)}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
            생성일: {formatDate(new Date())}
          </Typography>
        </Box>

        <Divider sx={{ mb: 4, borderColor: '#ddd' }} />

        {/* 요약 정보 */}
        <Box className="print-section" sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#000' }}>
            📊 생산 실적 요약
          </Typography>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
            {[
              { label: '총 생산량', value: summary.totalShots.toLocaleString(), unit: '쇼트' },
              { label: '출고 횟수', value: summary.totalCheckOuts.toLocaleString(), unit: '건' },
              { label: '사용 금형', value: summary.totalMolds.toLocaleString(), unit: '개' },
              { label: '평균 생산량/건', value: summary.averageShots.toLocaleString(), unit: '쇼트' },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  flex: 1,
                  minWidth: '120px',
                  p: 2,
                  border: '1px solid',
                  borderColor: '#ddd',
                  borderRadius: 1,
                  textAlign: 'center',
                  backgroundColor: '#f9f9f9',
                }}
              >
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {item.label}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5, color: '#000' }}>
                  {item.value}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {item.unit}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* 차트 영역 */}
        {productionData.length > 0 && (
          <>
            {/* 일별 생산 추이 */}
            <Box className="print-chart" sx={{ mb: 4 }} ref={dailyChartRef}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#000' }}>
                📈 일별 생산 추이
              </Typography>
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  p: 2,
                  bgcolor: 'white',
                }}
              >
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={dailyChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="date" stroke="#666" style={{ fontSize: '0.75rem' }} />
                    <YAxis stroke="#666" style={{ fontSize: '0.75rem' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="shots"
                      name="생산량 (쇼트)"
                      stroke="#1976d2"
                      strokeWidth={2}
                      dot={{ fill: '#1976d2', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Box>

            {/* 금형별 Top 10 */}
            <Box className="print-chart" sx={{ mb: 4 }} ref={topMoldsChartRef}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#000' }}>
                🏆 금형별 생산량 Top 10
              </Typography>
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  p: 2,
                  bgcolor: 'white',
                }}
              >
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={topMoldsChartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis type="number" stroke="#666" style={{ fontSize: '0.75rem' }} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      stroke="#666"
                      style={{ fontSize: '0.75rem' }}
                      width={80}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                      }}
                    />
                    <Legend />
                    <Bar dataKey="shots" name="생산량 (쇼트)" fill="#4caf50" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>

            {/* 고객사별 비율 */}
            <Box className="print-chart" sx={{ mb: 4 }} ref={customerChartRef}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#000' }}>
                🏢 고객사별 생산 비율
              </Typography>
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  p: 2,
                  bgcolor: 'white',
                }}
              >
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={customerChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry: any) =>
                        `${entry.name}: ${(entry.percent * 100).toFixed(1)}%`
                      }
                      outerRadius={100}
                      dataKey="value"
                    >
                      {customerChartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={['#1976d2', '#4caf50', '#ff9800', '#e91e63', '#9c27b0'][index % 5]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                      }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </>
        )}

        {/* 금형별 생산 실적 테이블 */}
        <Box className="print-section" sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#000' }}>
            🔧 금형별 생산 실적
          </Typography>
          <Box
            sx={{
              border: '1px solid',
              borderColor: '#ddd',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '40px 1fr 1.5fr 1fr 1fr 1fr 1fr',
                bgcolor: '#f0f0f0',
                p: 1.5,
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#000',
              }}
            >
              <Box></Box>
              <Box>금형번호</Box>
              <Box>금형명</Box>
              <Box sx={{ textAlign: 'center' }}>고객사</Box>
              <Box sx={{ textAlign: 'right' }}>출고횟수</Box>
              <Box sx={{ textAlign: 'right' }}>총 쇼트</Box>
              <Box sx={{ textAlign: 'right' }}>평균/회</Box>
            </Box>
            {/* 데이터 표시 */}
            {loading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress size={40} />
                <Typography sx={{ mt: 2, color: '#666' }}>
                  데이터를 불러오는 중...
                </Typography>
              </Box>
            ) : productionData.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center', color: '#666' }}>
                {summary.totalCheckOuts === 0 
                  ? '선택한 기간에 생산 실적이 없습니다.'
                  : '리포트 생성 버튼을 클릭하여 데이터를 불러오세요'}
              </Box>
            ) : (
              productionData.map((item, index) => (
                <React.Fragment key={item.moldId}>
                  {/* 금형 행 */}
                  <Box
                    className="print-table-row"
                    onClick={() => handleRowClick(item.moldId)}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '40px 1fr 1.5fr 1fr 1fr 1fr 1fr',
                      p: 1.5,
                      fontSize: '0.875rem',
                      color: '#000',
                      borderBottom: '1px solid #eee',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: '#f9f9f9',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {expandedMoldId === item.moldId ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </Box>
                    <Box>{item.moldNumber}</Box>
                    <Box>{item.moldName}</Box>
                    <Box sx={{ textAlign: 'center' }}>{item.customerName}</Box>
                    <Box sx={{ textAlign: 'right' }}>{item.checkOutCount}회</Box>
                    <Box sx={{ textAlign: 'right' }}>{item.totalShots.toLocaleString()}</Box>
                    <Box sx={{ textAlign: 'right' }}>{item.averageShots.toLocaleString()}</Box>
                  </Box>

                  {/* 상세 내역 (펼쳐진 경우) */}
                  {expandedMoldId === item.moldId && (
                    <Box
                      sx={{
                        bgcolor: '#fafafa',
                        borderBottom: index < productionData.length - 1 ? '1px solid #eee' : 'none',
                        p: 2,
                      }}
                    >
                      {loadingDetails ? (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                          <CircularProgress size={24} />
                          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666' }}>
                            상세 내역을 불러오는 중...
                          </Typography>
                        </Box>
                      ) : item.transactions && item.transactions.length > 0 ? (
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#000' }}>
                            입출고 상세 내역 ({item.transactions.length}건)
                          </Typography>
                          <Box
                            sx={{
                              border: '1px solid #e0e0e0',
                              borderRadius: 1,
                              overflow: 'hidden',
                              bgcolor: 'white',
                            }}
                          >
                            {/* 상세 헤더 */}
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: '120px 120px 100px 1fr',
                                bgcolor: '#f5f5f5',
                                p: 1,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: '#000',
                              }}
                            >
                              <Box>출고일</Box>
                              <Box>입고일</Box>
                              <Box sx={{ textAlign: 'right' }}>쇼트</Box>
                              <Box>목적</Box>
                            </Box>
                            {/* 상세 데이터 */}
                            {item.transactions.map((trans, idx) => (
                              <Box
                                key={trans.id}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: '120px 120px 100px 1fr',
                                  p: 1,
                                  fontSize: '0.75rem',
                                  color: '#333',
                                  borderTop: idx > 0 ? '1px solid #f0f0f0' : 'none',
                                }}
                              >
                                <Box>
                                  {trans.timestamp
                                    ? formatDate(trans.timestamp.toDate())
                                    : '-'}
                                </Box>
                                <Box>
                                  {trans.actualReturn
                                    ? formatDate(new Date(trans.actualReturn))
                                    : '-'}
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  {trans.shots?.toLocaleString() || 0}
                                </Box>
                                <Box>{trans.purpose || '-'}</Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', py: 2 }}>
                          상세 내역이 없습니다.
                        </Typography>
                      )}
                    </Box>
                  )}
                </React.Fragment>
              ))
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductionReport;
